import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../ui/Icon';
import ZapMark from '../ui/ZapMark';
import { getPrimaryNav, isNavItemActive, isMoreActive, adminBackTarget } from './adminNav';
import { useAdminShell } from './AdminContext';
import { getUser } from '../../utils/authUtil';

/**
 * The Admin / Super-Admin top bar. Replaces the old left sidebar + hamburger navbar with one shared
 * frame: brand on the left, the full nav as a horizontal (scrollable) row from 900px, a compact
 * hamburger + drawer below that. Right side: (super admin only) a real feedback-inbox badge, and the
 * avatar, which opens the profile sheet — the same interaction the student shell already uses.
 */
export default function AdminHeader() {
  const { pathname, search } = useLocation();
  const user = getUser();
  const { isSuper, openNav, openProfile, newFeedbackCount } = useAdminShell();
  const nav = getPrimaryNav(isSuper);
  const back = adminBackTarget(pathname, isSuper);
  const moreActive = isMoreActive(isSuper, pathname, search);
  const feedbackHref = isSuper ? '/super-admin/feedback' : '/admin/feedback';

  return (
    <header className="az-appbar">
      <div className="az-appbar-inner">
        <div className="az-appbar-left">
          {back && (
            <Link to={back.to} className="az-appbar-back" aria-label={`Back to ${back.label}`}>
              <Icon name="chevron-left" size={22} /><span className="az-appbar-back-label">{back.label}</span>
            </Link>
          )}
          <Link to={isSuper ? '/super-admin' : '/admin/dashboard'} className="az-appbar-brand" aria-label="Zaptude home">
            <ZapMark size={28} />
            <span className="az-appbar-wordmark">Zaptude</span>
          </Link>
          {isSuper && <span className="az-appbar-role" aria-label="Super Admin"><span className="az-role-long" aria-hidden="true">Super Admin</span><span className="az-role-short" aria-hidden="true">SA</span></span>}
        </div>

        <nav className="az-topnav" aria-label="Primary">
          {/* Plain Link, not NavLink: NavLink appends its OWN 'active' class whenever the current
              pathname merely starts with `to`'s pathname (ignoring query strings) — which would mark
              Platform Stats/Colleges/Companies "active" on every /super-admin/* route at once. Active
              state here is computed once, correctly, by isNavItemActive (which does look at the query
              string for the tab-scoped items) — nothing else should layer its own guess on top. */}
          {nav.map(item => {
            const active = isNavItemActive(item, pathname, search);
            return (
              <Link key={item.path} to={item.path} className={`az-topnav-item${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
                <Icon name={item.icon} size={18} /><span>{item.label}</span>
              </Link>
            );
          })}
          <button type="button" className={`az-topnav-item az-topnav-more${moreActive ? ' active' : ''}`} onClick={openNav} aria-haspopup="dialog">
            <Icon name="more" size={18} /><span>More</span>
          </button>
        </nav>

        <div className="az-appbar-right">
          <Link to={feedbackHref} className="az-appbar-icon" aria-label={newFeedbackCount ? `Feedback inbox, ${newFeedbackCount} new` : 'Feedback'}>
            <Icon name="feedback" size={20} />
            {isSuper && newFeedbackCount > 0 && <span className="az-appbar-badge" aria-hidden="true">{newFeedbackCount > 9 ? '9+' : newFeedbackCount}</span>}
          </Link>
          <button type="button" className="az-appbar-avatar" onClick={openProfile} aria-label="Open your profile menu" aria-haspopup="dialog">
            {(user?.name || 'A')[0].toUpperCase()}
          </button>
        </div>
      </div>
    </header>
  );
}
